import React from 'react';
import {
  GitBranch,
  GitMerge,
  GitPullRequest,
  GitCommit,
  GitFork,
  Server,
  Shield,
  Settings,
  Users,
  Database,
  Check,
  AlertTriangle,
} from 'lucide-react';

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}>
    {children}
  </div>
);

const ProductVersioning = () => {
  const repositories = {
    core: {
      name: "Core Banking",
      mainRepo: "kg-core-banking",
      environments: [
        {
          name: "Production",
          branch: "main",
          pattern: "kg-core/client-{clientId}/prod",
          protection: ["Sign-off requis", "Tests E2E", "Audit sécurité"]
        },
        {
          name: "Test/UAT",
          branch: "release/*",
          pattern: "kg-core/client-{clientId}/test",
          protection: ["Tests intégration", "Validation client"]
        },
        {
          name: "Développement",
          branch: "develop",
          pattern: "kg-core/client-{clientId}/dev",
          protection: ["Tests unitaires", "Code review"]
        }
      ]
    }
  };

  const branchingRules = [
    {
      type: "Client-Specific",
      prefix: "client/",
      pattern: "client/{clientId}/{env}/{feature}",
      examples: [
        "client/bnp-ml/prod/v1.2.3",
        "client/bnp-ml/test/auth-update",
        "client/bnp-ml/dev/feature-123"
      ],
      rules: [
        "Une branche par client et environnement",
        "Isolation complète entre clients",
        "Environnements séparés par client"
      ]
    },
    {
      type: "Feature",
      prefix: "feature/",
      pattern: "feature/{clientId}/{ticket}-{description}",
      examples: [
        "feature/bnp-ml/KG-123-auth-update",
        "feature/all/KG-456-core-update"
      ],
      rules: [
        "Spécifique client ou commun",
        "Référence ticket JIRA obligatoire",
        "Description courte et claire"
      ]
    },
    {
      type: "Release",
      prefix: "release/",
      pattern: "release/{clientId}/v{X.Y.Z}",
      examples: [
        "release/bnp-ml/v1.2.3",
        "release/all/v2.0.0"
      ],
      rules: [
        "Version sémantique obligatoire",
        "Tests complets requis",
        "Documentation à jour"
      ]
    },
    {
      type: "Hotfix",
      prefix: "hotfix/",
      pattern: "hotfix/{clientId}/{ticket}-{description}",
      examples: [
        "hotfix/bnp-ml/KG-789-fix-auth",
        "hotfix/all/KG-101-security-patch"
      ],
      rules: [
        "Correction urgente uniquement",
        "Tests critiques obligatoires",
        "Déploiement accéléré possible"
      ]
    }
  ];

  const environmentWorkflow = [
    {
      env: "Développement",
      purpose: "Développement actif et tests unitaires",
      rules: [
        "Build automatique sur push",
        "Tests unitaires obligatoires",
        "Revue de code par pairs",
        "Intégration continue active"
      ],
      deploymentFrequency: "Continu"
    },
    {
      env: "Test/UAT",
      purpose: "Validation client et tests intégration",
      rules: [
        "Déploiement depuis release branch",
        "Tests d'intégration complets",
        "Validation fonctionnelle client",
        "Tests de non-régression"
      ],
      deploymentFrequency: "Sprint (2 semaines)"
    },
    {
      env: "Production",
      purpose: "Environnement client final",
      rules: [
        "Déploiement depuis main uniquement",
        "Validation complète requise",
        "Backup pré-déploiement",
        "Monitoring renforcé"
      ],
      deploymentFrequency: "Selon release"
    }
  ];

  const promotionRules = [
    {
      from: "Dev → Test",
      requirements: [
        "Code review complete",
        "Tests unitaires passants",
        "Documentation à jour",
        "Pas de dette technique majeure"
      ]
    },
    {
      from: "Test → Prod",
      requirements: [
        "Validation client formelle",
        "Tests end-to-end passants",
        "Performance validée",
        "Plan de rollback prêt"
      ]
    }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <Card>
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
          <h2 className="text-2xl font-bold text-center text-white">
            Gestion des Versions et Environnements
          </h2>
          <p className="text-center text-white mt-2 opacity-90">
            Guide opérationnel par produit et client
          </p>
        </div>

        <div className="p-6 space-y-8">
          {/* Structure des Repositories */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Database className="mr-2" />
              Repositories et Environnements
            </h3>
            {Object.values(repositories).map((repo, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-600 mb-3">{repo.name}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {repo.environments.map((env, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="flex items-center mb-2">
                        <Server className="w-4 h-4 mr-2 text-blue-500" />
                        <h5 className="font-medium">{env.name}</h5>
                      </div>
                      <div className="text-sm mb-2">
                        <p className="text-gray-600">Pattern:</p>
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                          {env.pattern}
                        </code>
                      </div>
                      <div className="space-y-1">
                        {env.protection.map((prot, i) => (
                          <div key={i} className="flex items-center text-sm text-gray-600">
                            <Shield className="w-4 h-4 mr-2 text-green-500" />
                            {prot}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Règles de Branches */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <GitBranch className="mr-2" />
              Règles de Branches
            </h3>
            <div className="space-y-4">
              {branchingRules.map((rule, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-600 mb-3">{rule.type}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Pattern:</p>
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm block mb-3">
                        {rule.pattern}
                      </code>
                      <p className="text-sm text-gray-600 mb-2">Exemples:</p>
                      <ul className="space-y-1">
                        {rule.examples.map((example, idx) => (
                          <li key={idx} className="text-sm text-gray-600">
                            <code className="bg-gray-100 px-2 py-0.5 rounded">
                              {example}
                            </code>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Règles:</p>
                      <ul className="space-y-2">
                        {rule.rules.map((r, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <Check className="w-4 h-4 mr-2 text-green-500" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Workflow des Environnements */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <GitMerge className="mr-2" />
              Workflow des Environnements
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {environmentWorkflow.map((env, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center mb-3">
                    <Settings className="w-5 h-5 mr-2 text-blue-500" />
                    <h4 className="font-medium">{env.env}</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{env.purpose}</p>
                  <ul className="space-y-2">
                    {env.rules.map((rule, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <Check className="w-4 h-4 mr-2 text-green-500" />
                        {rule}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 text-sm">
                    <span className="text-blue-600 font-medium">Fréquence: </span>
                    <span className="text-gray-600">{env.deploymentFrequency}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Règles de Promotion */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <GitPullRequest className="mr-2" />
              Règles de Promotion
            </h3>
            <div className="space-y-4">
              {promotionRules.map((promotion, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center mb-3">
                    <GitFork className="w-5 h-5 mr-2 text-blue-500" />
                    <h4 className="font-medium">{promotion.from}</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {promotion.requirements.map((req, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600">
                        <Check className="w-4 h-4 mr-2 text-green-500" />
                        {req}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Points d'Attention */}
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-500 mr-3 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-700 mb-2">Points d'Attention Critiques</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-yellow-800">
                  <div className="space-y-2">
                    <p className="flex items-center">
                      <span className="text-yellow-500 mr-2">•</span>
                      Ne jamais merger directement en production
                    </p>
                    <p className="flex items-center">
                      <span className="text-yellow-500 mr-2">•</span>
                      Maintenir l'isolation stricte entre clients
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="flex items-center">
                      <span className="text-yellow-500 mr-2">•</span>
                      Backup avant chaque promotion
                    </p>
                    <p className="flex items-center">
                      <span className="text-yellow-500 mr-2">•</span>
                      Toujours utiliser les patterns de nommage
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProductVersioning;